import React, { useState, useCallback, useMemo, memo } from "react";
import { Location, TransportData } from "@/types/trip";
import AddDestinationModal from "@tripbuilder/new-location/addDestinationModal";
import useTrip from "@/stores/tripBuilder/useTrip";
import useLocationManager from "@/hooks/useLocationManager";

// Components
import LocationItem from "./LocationItem";
import DeleteOriginModal from "./DeleteOriginWarning";

interface SidebarProps {
  isModalOpen: boolean;
  transportData: TransportData;
  activeTransportButtonId: string | null;
  handleTransportButtonClick: (location: Location) => void;
  setIsModalOpen: (isOpen: boolean) => void;
}

const TripSidebar: React.FC<SidebarProps> = memo(
  ({
    isModalOpen,
    transportData,
    activeTransportButtonId,
    handleTransportButtonClick,
    setIsModalOpen,
  }) => {
    const { handleLocationClick } = useLocationManager();
    const [isDeleteOriginModalOpen, setDeleteOriginModalOpen] = useState(false);
    const tripData = useTrip((state) => state.tripData);
    const addLocation = useTrip((state) => state.addLocation);
    const selectedLocation = useTrip((state) => state.selectedLocation);
    const checkIsAdmin = useTrip((state) => state.checkIsAdmin);
    const { removeLocationById } = useLocationManager();

    const isAdmin = useMemo(() => checkIsAdmin(), [checkIsAdmin]);

    const locations = useMemo<Location[]>(
      () => tripData?.locations || [],
      [tripData?.locations]
    );

    const closeDeleteOriginModal = useCallback(() => {
      setDeleteOriginModalOpen(false);
    }, []);

    const selectedLocationId = useMemo(
      () => selectedLocation?.id || "",
      [selectedLocation?.id]
    );

    const renderLocationItems = useMemo(
      () =>
        locations.map((location, index) => (
          <LocationItem
            key={location.id + index}
            location={location}
            locations={locations}
            index={index}
            isAdmin={isAdmin}
            transportData={transportData}
            activeTransportButtonId={activeTransportButtonId}
            selectedLocationId={selectedLocationId}
            handleLocationClick={handleLocationClick}
            handleTransportButtonClick={handleTransportButtonClick}
            setIsModalOpen={setIsModalOpen}
            removeLocationById={removeLocationById}
            data-tutorial="location-item"
          />
        )),
      [
        locations,
        transportData,
        isAdmin,
        activeTransportButtonId,
        selectedLocationId,
        handleLocationClick,
        handleTransportButtonClick,
        removeLocationById,
        setIsModalOpen,
      ]
    );

    return (
      <div
        className="location-sidebar h-full min-h-full relative flex-col items-center justify-start shrink-0 w-[7%]"
        data-tutorial="location-sidebar"
      >
        <div
          id="locationSidebarFull"
          className="w-full h-full min-h-full flex flex-col justify-between bg-white py-1"
        >
          <ul
            id="locationSidebarList"
            style={{ height: "calc(100dvh - 4.5rem)" }}
            className="w-full flex flex-col items-center font-medium overflow-y-auto no-scrollbar"
          >
            {renderLocationItems}
          </ul>
        </div>
        <div className="absolute bottom-0 w-full space-y-2">
          <AddDestinationModal
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
            addLocation={addLocation}
            handleLocationClick={handleLocationClick}
          />
          <DeleteOriginModal
            isOpen={isDeleteOriginModalOpen}
            onClose={closeDeleteOriginModal}
          />
        </div>
      </div>
    );
  }
);

TripSidebar.displayName = "TripSidebar";

export default TripSidebar;
